import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OwnerPageLayout from '../../components/layout/OwnerPageLayout';
import { searchMarketplace, toggleFavorite, getFavorites } from '../../services/marketplaceApi';
import '../../styles/marketplace.css';

const DEFAULT_AVATAR = '/icons/default-avatar.svg';

function MarketplaceCard({ profile, onFavorite, isFavorite, t }) {
    return (
        <div className={`mp-card ${profile.marketplaceStatus === 'top' ? 'card-top' : ''} ${profile.marketplaceStatus === 'featured' ? 'card-featured' : ''}`}>
            {profile.marketplaceStatus === 'top' && <div className="top-badge">{t('marketplace:topBadge')}</div>}
            {profile.isVerified && <span className="mp-verified" title={t('marketplace:verified')}>✓</span>}
            <img src={profile.photoUrl || DEFAULT_AVATAR} alt="" className="mp-card-avatar" onError={(e) => { e.target.src = DEFAULT_AVATAR; }} />
            <h3>{profile.displayName}</h3>
            {profile.tagline && <p className="mp-card-tagline">{profile.tagline}</p>}
            {(profile.city || profile.region) && (
                <p className="mp-card-location">📍 {[profile.city, profile.region].filter(Boolean).join(', ')}</p>
            )}
            {profile.topQualification && (
                <div className="mp-qual-badge" style={{ backgroundColor: profile.topQualification.color || '#666' }}>
                    {profile.topQualification.icon} {profile.topQualification.name || profile.topQualification.nameRu}
                </div>
            )}
            {profile.topAchievement && (
                <div className="mp-achievement-preview">
                    🏆 {profile.topAchievement.result} — {profile.topAchievement.title} ({profile.topAchievement.year})
                </div>
            )}
            <div className="mp-card-actions">
                <Link to={`/p/${profile.slug}`} className="sp-btn sp-btn--primary">{t('marketplace:viewProfile')}</Link>
                <button type="button" className="sp-btn sp-btn--outline" onClick={() => onFavorite(profile.id, !isFavorite)}>
                    {isFavorite ? '♥' : '♡'}
                </button>
            </div>
        </div>
    );
}

function MarketplaceFilters({ filters, onChange, t }) {
    return (
        <div className="mp-filters">
            <input className="sp-input" placeholder={t('marketplace:filters.city')} value={filters.city || ''} onChange={(e) => onChange({ ...filters, city: e.target.value })} />
            <input className="sp-input" placeholder={t('marketplace:filters.region')} value={filters.region || ''} onChange={(e) => onChange({ ...filters, region: e.target.value })} />
            <select className="sp-select" value={filters.sortBy || 'score'} onChange={(e) => onChange({ ...filters, sortBy: e.target.value })}>
                <option value="score">{t('marketplace:filters.sortScore')}</option>
                <option value="recent">{t('marketplace:filters.sortRecent')}</option>
                <option value="achievements">{t('marketplace:filters.sortAchievements')}</option>
            </select>
            <button type="button" className="sp-btn sp-btn--outline" onClick={() => onChange({ ...filters, _refresh: Date.now() })}>{t('marketplace:search')}</button>
        </div>
    );
}

export default function MarketplacePage() {
    const { t } = useTranslation(['marketplace', 'common', 'nav']);
    const [activeTab, setActiveTab] = useState('all');
    const [filters, setFilters] = useState({ sortBy: 'score', page: 1, pageSize: 24 });
    const [results, setResults] = useState([]);
    const [total, setTotal] = useState(0);
    const [favoriteIds, setFavoriteIds] = useState(new Set());
    const token = localStorage.getItem('token');

    const load = useCallback(async () => {
        const type = activeTab === 'all' ? undefined : activeTab;
        const data = await searchMarketplace({ ...filters, type });
        setResults(data.items || []);
        setTotal(data.total || 0);
    }, [activeTab, filters]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!token) return;
        getFavorites().then((rows) => setFavoriteIds(new Set((rows || []).map((r) => r.sportProfileId).filter(Boolean))));
    }, [token]);

    async function onFavorite(id, add) {
        if (!token) { window.location.href = '/login'; return; }
        await toggleFavorite(id, add);
        setFavoriteIds((prev) => {
            const next = new Set(prev);
            if (add) next.add(id); else next.delete(id);
            return next;
        });
    }

    const tabs = [
        { key: 'all', label: t('marketplace:all') },
        { key: 'athlete', label: t('marketplace:athletes') },
        { key: 'coach', label: t('marketplace:coaches') },
        { key: 'freelance_coach', label: t('marketplace:freelance') },
        { key: 'school', label: t('marketplace:schools') },
    ];

    return (
        <OwnerPageLayout title={t('marketplace:title')} className="mp-page" maxWidth={1100}>
            <div className="mp-hero">
                <h1>🏆 SportProgress</h1>
                <p>{t('marketplace:heroSubtitle')}</p>
                {token && (
                    <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Link to="/profile/sport" className="sp-btn sp-btn--outline">{t('marketplace:profileEdit.title')}</Link>
                        <Link to="/profile/marketplace-settings" className="sp-btn sp-btn--outline">{t('marketplace:visibility.title')}</Link>
                    </div>
                )}
            </div>

            <div className="mp-tabs">
                {tabs.map((tab) => (
                    <button key={tab.key} type="button" className={activeTab === tab.key ? 'tab-active' : 'tab'} onClick={() => setActiveTab(tab.key)}>
                        {tab.label}
                    </button>
                ))}
            </div>

            <MarketplaceFilters filters={filters} onChange={setFilters} t={t} />

            <p style={{ padding: '0 16px', color: 'var(--sp-gray-600)' }}>{t('marketplace:resultsCount', { count: total })}</p>

            <div className="mp-grid">
                {results.map((profile) => (
                    <MarketplaceCard
                        key={`${profile.id}-${profile.slug || profile.displayName}`}
                        profile={profile}
                        onFavorite={onFavorite}
                        isFavorite={favoriteIds.has(profile.id)}
                        t={t}
                    />
                ))}
            </div>
            {!results.length && <p style={{ padding: 16 }}>{t('marketplace:noResults')}</p>}
        </OwnerPageLayout>
    );
}
